import styles from './2fa.module.scss';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Timer } from '@monorepo/base/src/components/timer/timer';
import { Input } from '@monorepo/base/src/components/form/input/input';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import routes from '../../../modules/routes/index.routes';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IMFPPStore } from '../../../modules/stores';
import { sessionStorageHelper } from '@monorepo/tools/src/lib/tools/storage';
import { URL_TO_RETURN_AFTER_LOGOUT } from '../../../lib/consts/storage';
import { OtpMethod, UserModel } from '../../../modules/models/user.model';
import { HttpHeaders } from '@monorepo/tools/src/lib/enums/headers';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { FullResponse } from '@monorepo/tools/src/lib/interfaces/url';
import { IUserRes } from '../../../modules/stores/users/user-crud.store';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

export const TwoFactorAuth: FC = observer(() => {
	const { userStore, authStore } = useStores<IMFPPStore>();
	const crudStore = userStore.getCrud();
	const user = crudStore.getData();
	const formStore = crudStore.getFormStore();
	const error = crudStore.getHttpError();
	const isLoading = crudStore.getIsLoading();
	const navigate = useNavigateParams();
	const params = new URLSearchParams(useLocation().search);
	const userId = params.get('userId');
	const qrCode = params.get('qrCode');
	const [method, setMethod] = useState<OtpMethod>((params.get('method') as OtpMethod) || OtpMethod.Email);
	const allowedMethods = (params.get('allowedMethods')?.split(',') as OtpMethod[]) || [OtpMethod.Email];
	const [isVerifiedOnce, setIsVerifiedOnce] = useState<boolean>(false);
	const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);
	const [timerKey, setTimerKey] = useState<number>(0);

	useEffect(() => {
		crudStore.reset();
	}, []);

	const onVerify = async ({ userId, token, method }: { userId: string; token: string; method: OtpMethod }) => {
		setIsVerifiedOnce(true);
		const signInProps = await crudStore.validate2fa({ userId, token, method });
		if (!signInProps) {
			return;
		}
		const { body, headers } = signInProps as FullResponse<IUserRes>;
		if (!body?.payload?.user) {
			return;
		}
		const userAuthToken = headers?.find(h => h[0] === HttpHeaders.USER_TOKEN)?.[1];
		if (!userAuthToken) {
			return;
		}
		const user = new UserModel(body.payload.user);
		userStore.setUser(user);
		authStore.setToken(userAuthToken);

		// if (!user.hasAccounts()) {
		// 	return navigate(routes.accounts.createAccount());
		// }

		if (!user.isVerified()) {
			return navigate(routes.auth.verify());
		}

		const urlToReturnTo = sessionStorageHelper.getItem(URL_TO_RETURN_AFTER_LOGOUT);
		navigate(urlToReturnTo || routes.general.home());
	};

	const onResend = async (userId: string, method: OtpMethod) => {
		await crudStore.resend2fa(userId, method);
	};

	const renderMethodInstruction = () => {
		const messages = [];
		if (method === OtpMethod.Email) {
			messages.push('Enter a 6-digit verification code sent to your email');
			messages.push('Please check your email for the code.');
		} else if (method === OtpMethod.Authenticator) {
			messages.push('Enter a 6-digit verification code from your');
			messages.push('Google Authenticator app');
		} else if (method === OtpMethod.SMS) {
			messages.push('Enter a 6-digit verification code sent to your phone');
			messages.push('Please check your phone for the code.');
		}

		return messages;
	};
	return (
		<div className={styles.mainWrapper}>
			<Helmet>
				<title>Manifesto - Login</title>
			</Helmet>

			<div className={styles.title}>Verify it's you.</div>
			{qrCode ? (
				<div>
					<div className={styles.subtitle}>
						<span>Please scan this QR-CODE in </span>
						<span>Google Authenticator app.</span>
					</div>
					<div className={styles.qrWrapper}>
						<img src={qrCode} alt="qr code" />
					</div>
				</div>
			) : null}
			<div className={styles.subtitle}>
				{renderMethodInstruction().map((message, index) => (
					<span key={index}>{message}</span>
				))}
				{allowedMethods?.length > 1 ? (
					<div
						className={styles.switch2fa}
						onClick={() => {
							if (userId && method) {
								crudStore.setHttpError(null);
								setIsResendDisabled(true);
								setTimerKey(prevKey => prevKey + 1);
								const newMethod = method === OtpMethod.Email ? OtpMethod.SMS : OtpMethod.Email;
								setMethod(newMethod);
								console.log(newMethod);
								onResend(userId, newMethod);
							}
						}}>
						Change to OTP by {method === OtpMethod.Email ? OtpMethod.SMS : OtpMethod.Email}&nbsp;
					</div>
				) : null}
			</div>

			<div className={styles.formWrapper}>
				<div className={styles.inputWrapper}>
					<Input
						autoFocus={true}
						className={styles.formInput}
						value={user.getCode()}
						placeholder={`Enter your code send by ${method}`}
						onValue={v => {
							if (Number(v) >= 0 || v === '') {
								user.setCode(v);
							}
							if (user.getCode()?.length === 6 && !isVerifiedOnce) {
								onVerify({ userId: userId + '', token: user.getCode() || '', method: method || 'email' });
							}
						}}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								onVerify({ userId: userId + '', token: user.getCode() || '', method: method || 'email' });
							}
						}}
						requiredAsterisk={false}
						type={'text'}
						error={formStore.getErrors().get('code')?.getMsg()}
						maxLength={6}
						minLength={6}
					/>
				</div>
				<PrimaryButton
					isRotateIcon={isLoading}
					iconSize={'20px'}
					icon={isLoading ? 'loading-01' : ''}
					className={styles.verifyBtn}
					onClick={() =>
						onVerify({
							userId: userId + '',
							token: user.getCode() || '',
							method: method || 'email',
						})
					}>
					Verify
				</PrimaryButton>
				{error ? <ErrorMsg error={error} isIcon={false} classes={{ error: styles.error }} /> : null}
				<span className={styles.resend2Fa}>
					Didn't receive your 2FA code?&nbsp;
					<span
						className={`${styles.cta} ${!userId || isResendDisabled ? styles.disabled : ''}`}
						onClick={() => {
							if (userId && method) {
								crudStore.setHttpError(null);
								setIsResendDisabled(true);
								setTimerKey(prevKey => prevKey + 1);
								onResend(userId, method);
							}
						}}>
						Click here to resend code.
					</span>
					<div className={styles.timerWrapper}>
						<Timer
							classNames={{ timerWrapper: styles.compTimerWrapper }}
							isDisappeaerOnTimeup={true}
							key={timerKey}
							initialTime={60}
							handleTimeup={() => setIsResendDisabled(false)}
							beforeText={'(You can request a new code in '}
							afterText={' seconds)'}
						/>
					</div>
				</span>
			</div>
		</div>
	);
});
